import { apiService, ApiResponse } from './api.service';

export interface PermissionItem {
  id: string;
  key: string;
  label: string;
  category: string;
  description?: string;
  isSystem: boolean;
}

export interface RolePermissionItem {
  id: string;
  permission: {
    id: string;
    key: string;
    label: string;
    category: string;
  };
}

export interface RbacRole {
  id: string;
  code: string;
  name: string;
  description?: string;
  scope: 'PLATFORM' | 'TENANT';
  tenantId?: string | null;
  isSystem: boolean;
  isActive: boolean;
  rolePermissions: RolePermissionItem[];
  _count?: {
    assignments: number;
  };
}

export interface CreateRoleDto {
  name: string;
  code: string;
  description?: string;
  scope?: 'PLATFORM' | 'TENANT';
  tenantId?: string;
  permissionKeys?: string[];
}

export interface SetRolePermissionsDto {
  permissionKeys: string[];
}

export interface AssignUserRolesDto {
  roleIds: string[];
  tenantId?: string;
}

class RbacService {
  async getPermissions(): Promise<ApiResponse<PermissionItem[]>> {
    return apiService.get<PermissionItem[]>('/rbac/permissions');
  }

  async getRoles(params?: { scope?: 'PLATFORM' | 'TENANT'; tenantId?: string }): Promise<ApiResponse<RbacRole[]>> {
    const search = new URLSearchParams();
    if (params?.scope) search.append('scope', params.scope);
    if (params?.tenantId) search.append('tenantId', params.tenantId);
    const qs = search.toString();
    return apiService.get<RbacRole[]>(`/rbac/roles${qs ? `?${qs}` : ''}`);
  }

  async createRole(payload: CreateRoleDto): Promise<ApiResponse<RbacRole>> {
    return apiService.post<RbacRole>('/rbac/roles', payload);
  }

  async setRolePermissions(roleId: string, payload: SetRolePermissionsDto): Promise<ApiResponse<RbacRole>> {
    return apiService.patch<RbacRole>(`/rbac/roles/${roleId}/permissions`, payload);
  }

  async assignUserRoles(userId: string, payload: AssignUserRolesDto): Promise<ApiResponse<string[]>> {
    return apiService.patch<string[]>(`/rbac/users/${userId}/roles`, payload);
  }

  async getMyPermissions(): Promise<ApiResponse<string[]>> {
    return apiService.get<string[]>('/rbac/me/permissions');
  }
}

export const rbacService = new RbacService();
